"use client"

import React, { useEffect, useState } from "react"

import { Button } from "@/shadcn/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shadcn/components/ui/card"
import {
  Field,
  FieldLabel,
  FieldError as ShadFieldError,
} from "@/shadcn/components/ui/field"
import { Input } from "@/shadcn/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn/components/ui/select"
import { cn } from "@/lib/utils"
import ErrorModule from "app/mixins/ErrorModule.jsx"
import FieldError from "app/components/common/FieldError.jsx"

const SM_BUTTON =
  "tw:h-7 tw:rounded-md tw:px-2.5 tw:gap-1 tw:shadow-none"

const CARD =
  "tw:gap-0 tw:py-0 tw:ring-0 tw:border tw:border-border tw:shadow-xs tw:rounded-xl tw:bg-card tw:overflow-hidden"
const CARD_HEADER = "tw:p-4 tw:gap-1"
const CARD_TITLE =
  "tw:text-base tw:font-medium tw:leading-6 tw:text-card-foreground"
const CARD_DESCRIPTION =
  "tw:text-sm tw:font-normal tw:leading-5 tw:text-muted-foreground"

const FIELD_LABEL =
  "tw:text-sm tw:font-medium tw:leading-5 tw:text-foreground"
const FIELD_INPUT =
  "tw:h-8 tw:rounded-lg tw:px-2.5 tw:py-1 tw:text-sm tw:font-normal tw:leading-5"
const SELECT_TRIGGER =
  "tw:h-8 tw:rounded-lg tw:border-input tw:px-2.5 tw:py-1 tw:text-sm tw:font-normal tw:leading-5"

export type CardBrand =
  | "Visa"
  | "MasterCard"
  | "American Express"
  | "Discover"
  | "Apple Pay"
  | "paypal"
  | undefined

export interface Payment {
  cardType?: CardBrand
  last?: number
  month?: number
  type: "trial" | "paypal" | "card"
  year?: number
  agreement?: string
}

export interface BillingInfo {
  companyName?: string
  address1?: string
  address2?: string
  city?: string
  state?: string
  zip?: string
  country?: string
  phone?: string
  vat_number?: string
  firstName?: string
  lastName?: string
  email?: string
  payment: Payment
}

export interface SelectOption {
  label: string
  value: string
}

interface BillingInfoPageProps {
  app: any
  billingInfo: BillingInfo
  countries: SelectOption[]
  states: SelectOption[]
  isEditing: {
    billingContact: boolean
    billingAddress: boolean
  }
  onBeginEdit: (section: "billingContact" | "billingAddress") => void
  onCancelEdit: (section: "billingContact" | "billingAddress") => void
  onSaveContact: (contact: BillingInfo) => void
  onSaveAddress: (address: BillingInfo) => void
  onUpdatePayment: () => void
  onCountryChange?: (countryId: string) => void
  isFieldInvalid?: (field: string) => boolean
}

export default function BillingInfoPage(props: BillingInfoPageProps) {
  return (
    <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-[968px] tw:flex-col tw:gap-4 tw:p-4 tw:font-sans tw:md:p-6">
      <PageHeading
        title="Billing Information"
        description="Manage your payment method and billing details."
      />

      <PaymentMethodCard
        paymentMethod={props.billingInfo.payment ?? null}
        onUpdate={props.onUpdatePayment}
      />

      <BillingContactCard
        contact={props.billingInfo}
        isEditing={props.isEditing.billingContact}
        onBeginEdit={() => props.onBeginEdit("billingContact")}
        onCancelEdit={() => props.onCancelEdit("billingContact")}
        onSave={props.onSaveContact}
        app={props.app}
        isFieldInvalid={props.isFieldInvalid}
      />

      <BillingAddressCard
        address={props.billingInfo}
        countries={props.countries}
        states={props.states}
        isEditing={props.isEditing.billingAddress}
        onBeginEdit={() => props.onBeginEdit("billingAddress")}
        onCancelEdit={() => props.onCancelEdit("billingAddress")}
        onSave={props.onSaveAddress}
        onCountryChange={props.onCountryChange}
        app={props.app}
        isFieldInvalid={props.isFieldInvalid}
      />
    </div>
  )
}

function PageHeading({
  title,
  description,
}: {
  title: string
  description: string
}) {
  return (
    <header className="tw:flex tw:flex-col tw:gap-1 tw:py-4">
      <h1 className="tw:text-2xl tw:font-semibold tw:leading-8 tw:text-foreground">
        {title}
      </h1>
      <p className="tw:text-sm tw:font-medium tw:leading-5 tw:text-muted-foreground">
        {description}
      </p>
    </header>
  )
}

function PaymentMethodCard({
  paymentMethod,
  onUpdate,
}: {
  paymentMethod: Payment | null
  onUpdate: () => void
}) {
  return (
    <Card className={CARD}>
      <CardHeader
        className={cn(CARD_HEADER, "tw:flex tw:flex-row tw:items-center")}
      >
        <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-1">
          <CardTitle className={CARD_TITLE}>Payment method</CardTitle>
          <CardDescription className={CARD_DESCRIPTION}>
            Card used for your dlvr.it subscription.
          </CardDescription>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(SM_BUTTON, "tw:shrink-0")}
          onClick={onUpdate}
        >
          {paymentMethod ? "Update payment method" : "Add payment method"}
        </Button>
      </CardHeader>
      {paymentMethod ? (
        <CardContent className="tw:flex tw:flex-col tw:gap-0 tw:px-4 tw:pb-4">
          <div className="tw:flex tw:items-start tw:gap-2 tw:rounded-lg tw:px-3 tw:py-2.5">
            <span className="tw:flex tw:size-5 tw:shrink-0 tw:items-center tw:justify-center">
              {paymentMethod.type === "paypal" ? (
                <BrandIcon brand={paymentMethod.type} />
              ) : (
                <BrandIcon brand={paymentMethod.cardType} />
              )}

            </span>
            <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:justify-center tw:gap-1">
              <p className="tw:text-sm tw:font-medium tw:leading-4 tw:text-foreground">
                {formatPaymentLine(paymentMethod)}
              </p>
              {paymentMethod.type !== "paypal" &&
              paymentMethod.month &&
              paymentMethod.year ? (
                <p className="tw:truncate tw:text-sm tw:font-normal tw:leading-5 tw:text-muted-foreground">
                  Expires {formatExpiry(paymentMethod)}
                </p>
              ) : null}
            </div>
          </div>
        </CardContent>
      ) : null}
    </Card>
  )
}

function formatPaymentLine(p: Payment): string {
  if (p.type === "paypal") {
    return `Paypal agreement ${p.agreement}`;
  } else {
    return `${p.cardType} ending in ${p.last ?? ""}`;
  }
}

function formatExpiry(p: Payment): string {
  const month = String(p.month ?? "").padStart(2, "0")
  return `${month}/${p.month}`
}

function BillingContactCard({
  contact,
  isEditing,
  onBeginEdit,
  onCancelEdit,
  onSave,
  app,
  isFieldInvalid,
}: {
  contact: BillingInfo
  isEditing: boolean
  onBeginEdit: () => void
  onCancelEdit: () => void
  onSave: (next: BillingInfo) => void
  app: any
  isFieldInvalid?: (field: string) => boolean
}) {
  const [draft, setDraft] = useState<BillingInfo>(contact)

  useEffect(() => {
    if (isEditing) setDraft(contact)
  }, [isEditing, contact])

  function update<K extends keyof BillingInfo>(
    key: K,
    value: BillingInfo[K]
  ) {
    setDraft((d) => ({ ...d, [key]: value }))
  }

  return (
    <Card className={CARD}>
      <CardHeader
        className={cn(CARD_HEADER, "tw:flex tw:flex-row tw:items-start")}
      >
        <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-1">
          <CardTitle className={CARD_TITLE}>Billing Contact</CardTitle>
          <CardDescription className={CARD_DESCRIPTION}>
            Receives invoices and billing notifications.
          </CardDescription>
        </div>
        {isEditing ? (
          <div className="tw:flex tw:shrink-0 tw:gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={SM_BUTTON}
              onClick={onCancelEdit}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              className={SM_BUTTON}
              onClick={() => onSave(draft)}
            >
              Save changes
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(SM_BUTTON, "tw:shrink-0")}
            onClick={onBeginEdit}
          >
            Edit
          </Button>
        )}
      </CardHeader>
      <CardContent className="tw:flex tw:flex-col tw:px-4 tw:pb-4">
        {isEditing ? (
          <form
            className="tw:flex tw:w-full tw:max-w-[672px] tw:flex-col tw:gap-6"
            onSubmit={(e) => {
              e.preventDefault()
              onSave(draft)
            }}
          >
            <div className="tw:grid tw:grid-cols-1 tw:items-start tw:gap-3 tw:md:grid-cols-2">
              <Field data-invalid={isFieldInvalid?.("firstName") ?? false}>
                <FieldLabel htmlFor="billing-contact-first" className={FIELD_LABEL}>
                  First Name
                </FieldLabel>
                <Input
                  id="billing-contact-first"
                  defaultValue={contact.firstName}
                  onChange={(e) => update("firstName", e.target.value)}
                  className={FIELD_INPUT}
                />
                <ContactFieldError app={app} field="firstName" />
              </Field>
              <Field data-invalid={isFieldInvalid?.("lastName") ?? false}>
                <FieldLabel htmlFor="billing-contact-last" className={FIELD_LABEL}>
                  Last Name
                </FieldLabel>
                <Input
                  id="billing-contact-last"
                  defaultValue={contact.lastName}
                  onChange={(e) => update("lastName", e.target.value)}
                  className={FIELD_INPUT}
                />
                <ContactFieldError app={app} field="lastName" />
              </Field>
            </div>
            <Field data-invalid={isFieldInvalid?.("email") ?? false}>
              <FieldLabel htmlFor="billing-contact-email" className={FIELD_LABEL}>
                Email
              </FieldLabel>
              <Input
                id="billing-contact-email"
                type="email"
                defaultValue={contact.email}
                placeholder="email@email.com"
                onChange={(e) => update("email", e.target.value)}
                className={FIELD_INPUT}
              />
              <ContactFieldError app={app} field="email" />
            </Field>
          </form>
        ) : (
          <div className="tw:flex tw:flex-col tw:gap-1 tw:px-3 tw:py-2.5">
            <p className="tw:text-sm tw:font-medium tw:leading-4 tw:text-foreground">
              {[contact.firstName, contact.lastName].filter(Boolean).join(" ") ||
                "—"}
            </p>
            <p className="tw:truncate tw:text-sm tw:font-normal tw:leading-5 tw:text-muted-foreground">
              {contact.email}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function ContactFieldError({ app, field }: { app: any; field: string }) {
  return (
    <ErrorModule
      app={app}
      keys={["SUB_UPDATE_BILLING"]}
      render={(state: any) => (
        <FieldError field={field} errors={state.errors} element={ShadFieldError} />
      )}
    />
  )
}

function BillingAddressCard({
  address,
  countries,
  states,
  isEditing,
  onBeginEdit,
  onCancelEdit,
  onSave,
  onCountryChange,
  app,
  isFieldInvalid,
}: {
  address: BillingInfo
  countries: SelectOption[]
  states: SelectOption[]
  isEditing: boolean
  onBeginEdit: () => void
  onCancelEdit: () => void
  onSave: (next: BillingInfo) => void
  onCountryChange?: (countryId: string) => void
  app: any
  isFieldInvalid?: (field: string) => boolean
}) {
  const [draft, setDraft] = useState<BillingInfo>(address)

  useEffect(() => {
    if (isEditing) setDraft(address)
  }, [isEditing, address])

  function update<K extends keyof BillingInfo>(
    key: K,
    value: BillingInfo[K]
  ) {
    setDraft((d) => ({ ...d, [key]: value }))
  }

  return (
    <Card className={CARD}>
      <CardHeader
        className={cn(CARD_HEADER, "tw:flex tw:flex-row tw:items-start")}
      >
        <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-1">
          <CardTitle className={CARD_TITLE}>Billing Address</CardTitle>
          <CardDescription className={CARD_DESCRIPTION}>
            Appears on your invoices and receipts.
          </CardDescription>
        </div>
        {isEditing ? (
          <div className="tw:flex tw:shrink-0 tw:gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={SM_BUTTON}
              onClick={onCancelEdit}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              className={SM_BUTTON}
              onClick={() => onSave(draft)}
            >
              Save changes
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(SM_BUTTON, "tw:shrink-0")}
            onClick={onBeginEdit}
          >
            Edit
          </Button>
        )}
      </CardHeader>
      <CardContent className="tw:flex tw:flex-col tw:px-4 tw:pb-4">
        {isEditing ? (
          <form
            className="tw:flex tw:w-full tw:max-w-[672px] tw:flex-col tw:gap-6"
            onSubmit={(e) => {
              e.preventDefault()
              onSave(draft)
            }}
          >
            <Field data-invalid={isFieldInvalid?.("companyName") ?? false}>
              <FieldLabel htmlFor="billing-address-company" className={FIELD_LABEL}>
                Company
              </FieldLabel>
              <Input
                id="billing-address-company"
                defaultValue={address.companyName}
                onChange={(e) => update("companyName", e.target.value)}
                className={FIELD_INPUT}
              />
              <AddressFieldError app={app} field="companyName" />
            </Field>

            <Field data-invalid={isFieldInvalid?.("address1") ?? false}>
              <FieldLabel htmlFor="billing-address-1" className={FIELD_LABEL}>
                Address
              </FieldLabel>
              <Input
                id="billing-address-1"
                defaultValue={address.address1}
                onChange={(e) => update("address1", e.target.value)}
                className={FIELD_INPUT}
              />
              <AddressFieldError app={app} field="address1" />
            </Field>

            <Field data-invalid={isFieldInvalid?.("address2") ?? false}>
              <FieldLabel htmlFor="billing-address-2" className={FIELD_LABEL}>
                Apartment, suite, etc.
              </FieldLabel>
              <Input
                id="billing-address-2"
                defaultValue={address.address2}
                onChange={(e) => update("address2", e.target.value)}
                className={FIELD_INPUT}
              />
              <AddressFieldError app={app} field="address2" />
            </Field>

            <div className="tw:grid tw:grid-cols-1 tw:items-start tw:gap-4 tw:md:grid-cols-2">
              <Field data-invalid={isFieldInvalid?.("city") ?? false}>
                <FieldLabel htmlFor="billing-address-city" className={FIELD_LABEL}>
                  City
                </FieldLabel>
                <Input
                  id="billing-address-city"
                  defaultValue={address.city}
                  onChange={(e) => update("city", e.target.value)}
                  className={FIELD_INPUT}
                />
                <AddressFieldError app={app} field="city" />
              </Field>
              <Field data-invalid={isFieldInvalid?.("country") ?? false}>
                <FieldLabel htmlFor="billing-address-country" className={FIELD_LABEL}>
                  Country
                </FieldLabel>
                <Select
                  value={draft.country}
                  onValueChange={(v) => {
                    update("country", v)
                    onCountryChange?.(v)
                  }}
                >
                  <SelectTrigger
                    id="billing-address-country"
                    className={SELECT_TRIGGER}
                  >
                    <SelectValue placeholder="Select a country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <AddressFieldError app={app} field="country" />
              </Field>
            </div>

            <div className="tw:grid tw:grid-cols-1 tw:items-start tw:gap-4 tw:md:grid-cols-2">
              <Field data-invalid={isFieldInvalid?.("state") ?? false}>
                <FieldLabel htmlFor="billing-address-state" className={FIELD_LABEL}>
                  State / Province
                </FieldLabel>
                {states.length ? (
                  <Select
                    value={draft.state}
                    onValueChange={(v) => update("state", v)}
                  >
                    <SelectTrigger
                      id="billing-address-state"
                      className={SELECT_TRIGGER}
                    >
                      <SelectValue placeholder="Select a state" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="billing-address-state"
                    defaultValue={address.state}
                    onChange={(e) => update("state", e.target.value)}
                    className={FIELD_INPUT}
                  />
                )}
                <AddressFieldError app={app} field="state" />
              </Field>
              <Field data-invalid={isFieldInvalid?.("zip") ?? false}>
                <FieldLabel htmlFor="billing-address-zip" className={FIELD_LABEL}>
                  Postal code
                </FieldLabel>
                <Input
                  id="billing-address-zip"
                  defaultValue={address.zip}
                  onChange={(e) => update("zip", e.target.value)}
                  className={FIELD_INPUT}
                />
                <AddressFieldError app={app} field="zip" />
              </Field>
            </div>

            <Field data-invalid={isFieldInvalid?.("phone") ?? false}>
              <FieldLabel htmlFor="billing-address-phone" className={FIELD_LABEL}>
                Phone
              </FieldLabel>
              <Input
                id="billing-address-phone"
                type="tel"
                defaultValue={address.phone}
                onChange={(e) => update("phone", e.target.value)}
                className={FIELD_INPUT}
              />
              <AddressFieldError app={app} field="phone" />
            </Field>

            <Field data-invalid={isFieldInvalid?.("vat_number") ?? false}>
              <FieldLabel htmlFor="billing-address-vat" className={FIELD_LABEL}>
                VAT
              </FieldLabel>
              <Input
                id="billing-address-vat"
                defaultValue={address.vat_number}
                onChange={(e) => update("vat_number", e.target.value)}
                className={FIELD_INPUT}
              />
              <AddressFieldError app={app} field="vat_number" />
            </Field>
          </form>
        ) : (
          <AddressDisplay address={address} countries={countries} />
        )}
      </CardContent>
    </Card>
  )
}

function AddressFieldError({ app, field }: { app: any; field: string }) {
  return (
    <ErrorModule
      app={app}
      keys={["SUB_UPDATE_BILLING"]}
      render={(state: any) => (
        <FieldError field={field} errors={state.errors} element={ShadFieldError} />
      )}
    />
  )
}

function AddressDisplay({
  address,
  countries,
}: {
  address: BillingInfo
  countries: SelectOption[]
}) {
  const countryName =
    countries.find((c) => c.value === address.country)?.label ?? address.country
  const cityRegionZip = [address.city, address.state, address.zip]
    .filter(Boolean)
    .join(" ")

  return (
    <div className="tw:flex tw:flex-col tw:gap-1 tw:px-3 tw:py-2.5">
      {address.companyName ? <Line text={address.companyName} /> : null}
      {address.address1 ? <Line text={address.address1} /> : null}
      {address.address2 ? <Line text={address.address2} /> : null}
      {cityRegionZip ? <Line text={cityRegionZip} /> : null}
      {countryName ? <Line text={countryName} /> : null}
      {address.vat_number ? <Line text={`VAT ${address.vat_number}`} /> : null}
      {address.phone ? <Line text={address.phone} /> : null}
    </div>
  )
}

function Line({ text }: { text: string }) {
  return (
    <p className="tw:truncate tw:text-sm tw:font-normal tw:leading-5 tw:text-muted-foreground">
      {text}
    </p>
  )
}

/* -------------------------------------------------------------------------- */
/* Brand icons — inline SVGs sized to the 20px Item / Media slot.             */
/* Card-shaped 24x16 viewBox keeps the brand mark inside a rounded tile.      */
/* -------------------------------------------------------------------------- */

function BrandIcon({ brand }: { brand: CardBrand }) {
  switch (brand) {
    case "Visa":
      return <VisaIcon />
    case "MasterCard":
      return <MastercardIcon />
    case "American Express":
      return <AmexIcon />
    case "Discover":
      return <DiscoverIcon />
    case "Apple Pay":
      return <ApplePayIcon />
    case "paypal":
      return <PaypalIcon />
    default:
      return null
  }
}

const CARD_BG = "tw:rounded-[3px] tw:border tw:border-border"

function VisaIcon() {
  return (
    <svg
      viewBox="0 0 24 16"
      width="20"
      height="14"
      aria-hidden
      role="img"
      className={CARD_BG}
    >
      <rect width="24" height="16" rx="2" fill="#ffffff" />
      <text
        x="12"
        y="11"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontSize="7"
        fontWeight="800"
        fontStyle="italic"
        fill="#1A1F71"
        letterSpacing="0.2"
      >
        VISA
      </text>
    </svg>
  )
}

function MastercardIcon() {
  return (
    <svg
      viewBox="0 0 24 16"
      width="20"
      height="14"
      aria-hidden
      role="img"
      className={CARD_BG}
    >
      <rect width="24" height="16" rx="2" fill="#ffffff" />
      <circle cx="10" cy="8" r="4" fill="#EB001B" />
      <circle cx="14" cy="8" r="4" fill="#F79E1B" fillOpacity="0.85" />
    </svg>
  )
}

function AmexIcon() {
  return (
    <svg
      viewBox="0 0 24 16"
      width="20"
      height="14"
      aria-hidden
      role="img"
      className={CARD_BG}
    >
      <rect width="24" height="16" rx="2" fill="#1F72CD" />
      <text
        x="12"
        y="11"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontSize="5"
        fontWeight="800"
        fill="#ffffff"
        letterSpacing="0.3"
      >
        AMEX
      </text>
    </svg>
  )
}

function DiscoverIcon() {
  return (
    <svg
      viewBox="0 0 24 16"
      width="20"
      height="14"
      aria-hidden
      role="img"
      className={CARD_BG}
    >
      <rect width="24" height="16" rx="2" fill="#ffffff" />
      <path d="M24 9 Q18 16 8 13 L24 13 Z" fill="#FF6B00" />
      <text
        x="12"
        y="8"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontSize="4"
        fontWeight="800"
        fill="#1A1A1A"
        letterSpacing="0.2"
      >
        DISCOVER
      </text>
    </svg>
  )
}

function ApplePayIcon() {
  return (
    <svg
      viewBox="0 0 24 16"
      width="20"
      height="14"
      aria-hidden
      role="img"
      className={CARD_BG}
    >
      <rect width="24" height="16" rx="2" fill="#ffffff" />
      <path
        d="M7.4 6.1c-.3.4-.8.7-1.3.6-.1-.5.2-1 .5-1.3.3-.4.9-.7 1.3-.7.1.5-.2 1-.5 1.4zm.5.8c-.7 0-1.3.4-1.7.4-.4 0-.9-.4-1.5-.4-.8 0-1.5.5-1.9 1.2-.8 1.4-.2 3.5.6 4.7.4.6.8 1.2 1.4 1.2.6 0 .8-.4 1.5-.4s.9.4 1.5.4c.6 0 1-.6 1.4-1.2.4-.6.6-1.2.6-1.3 0 0-1.2-.5-1.2-1.9 0-1.2 1-1.7 1-1.7-.5-.8-1.4-.9-1.7-1z"
        fill="#000000"
      />
      <text
        x="16"
        y="11"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontSize="5"
        fontWeight="700"
        fill="#000000"
      >
        Pay
      </text>
    </svg>
  )
}

function PaypalIcon() {
  return (
    <svg
      viewBox="0 0 24 16"
      width="20"
      height="14"
      aria-hidden
      role="img"
      className={CARD_BG}
    >
      <rect width="24" height="16" rx="2" fill="#ffffff" />
      <path
        d="M9.5 4h3.6c2 0 3.1 1 2.7 2.9-.4 2-1.9 2.9-3.7 2.9h-1.2l-.4 2.6H8.7L9.5 4z"
        fill="#003087"
      />
      <path
        d="M11.5 5.5h3.3c1.8 0 2.8.9 2.4 2.7-.4 1.8-1.7 2.7-3.4 2.7h-1.1l-.4 2.4h-1.9L11.5 5.5z"
        fill="#009CDE"
      />
    </svg>
  )
}
